import React from 'react'
import { useTranslation } from 'react-i18next'
import clipboard from 'rich-clipboard'
import { ClipboardType, RichLinkClipboardItem } from '~/clipboard'
import { Link } from '~/models'
import { ClearButton, HBox, TextField, VBox } from '~/ui/components'
import { useViewState } from '~/ui/hooks'
import { createUseStyles, layout, useTheme } from '~/ui/styling'
import { useChat } from './ChatContext'

export interface Props {
  requestUpload?: () => any
  enabled?:       boolean
}

const ChatInputBar = React.memo((props: Props) => {

  const {requestUpload, enabled: props_enabled} = props

  const {service: {currentChat}} = useChat()
  const enabled = props_enabled && currentChat != null

  const [t] = useTranslation('chat')
  const theme = useTheme()

  // Text is persisted per chat, so that a draft is not lost when navigating away and back
  // (e.g. to another page, or switching to another app and back).
  const [text, setText] = useViewState<string>(`chat.draft.${currentChat?.uri ?? 'none'}`, '')
  const [link, setLink] = React.useState<Link | null>()

  const inputRef = React.useRef<HTMLTextAreaElement>(null)

  React.useEffect(() => {
    const el = inputRef.current
    if (el == null) { return }
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [text])

  //------
  // Callbacks

  const sendMessage = React.useCallback(async () => {
    const clean = text.trim()
    if (clean === '') { return }

    setText('')
    setLink(null)

    const hasLink = link != null && text.includes(link.href)

    currentChat?.sendMessage({
      type: 'text',
      text: hasLink ? text.replace(link.href, '') : text,
      link: hasLink ? link : undefined,
    })
  }, [text, link, currentChat, setText])

  const handlePaste = React.useCallback((event: React.ClipboardEvent) => {
    const richLink = clipboard.getData<RichLinkClipboardItem>(ClipboardType.RICH_LINK, false)?.data
    if (richLink == null) { return }

    const newText = t('input_bar.paste.rich_link', {item: richLink})
    setText(text.length === 0 ? newText : `${text} ${newText}`)
    setLink(richLink.link)

    event.preventDefault()
    return
  }, [t, text, setText])

  //------
  // Typing

  React.useEffect(() => {
    if (currentChat == null) { return }
    if (text.trim() === '') {
      currentChat.stopTyping()
    } else {
      currentChat.startTyping()
    }
  }, [currentChat, text])

  //------
  // Rendering

  const $ = useStyles()

  function render() {
    return (
      <HBox gap={layout.padding.inline.m} classNames={$.chatInputBar} align='bottom'>
        <VBox flex>
          <TextField
            ref={inputRef}
            value={text}
            onChange={setText}
            onCommit={sendMessage}
            placeholder={t('input_bar.placeholder')}
            accessoryRight={renderAttachmentButton()}
            enabled={enabled}
            inputStyle='dark'
            multiline
            rows={1}
            resize='none'
            maxHeight={160}
            inputAttributes={{
              onPaste: handlePaste,
            }}
          />
        </VBox>
        {renderSendButton()}
      </HBox>
    )
  }

  function renderAttachmentButton() {
    if (requestUpload == null) { return null }

    return (
      <ClearButton
        icon='paperclip'
        onTap={requestUpload}
      />
    )
  }

  function renderSendButton() {
    return (
      <ClearButton
        icon='arrow-right'
        padding='none'
        align='center'
        enabled={enabled && text.trim() !== ''}
        onTap={sendMessage}
        backgroundColor={theme.semantic.primary}
        color={theme.inverse.fg.normal}
        classNames={$.sendButton}
      />
    )
  }

  return render()

})

export default ChatInputBar

const useStyles = createUseStyles(theme => ({
  chatInputBar: {
    padding:    layout.padding.inline.l,
    paddingTop: 0,
  },

  sendButton: {
    flexShrink:     0,
    alignSelf:      'flex-end',
    width:          36,
    height:         36,
    borderRadius:   18,
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'center',
    '&::before': {
      borderRadius: 18,
    },
  },
}))